import apiClient from './apiClient';
import {
  STORES,
  cacheMultiple,
  getAllCachedData,
  setMetadata,
  cacheLegacyData,
  getLegacyCachedData
} from '../utils/offlineDB';

// Kalender Events für Zeitraum abrufen (mit Offline-Fallback)
export const getKalenderEvents = async (start, end) => {
  try {
    const response = await apiClient.get('/kalender/events', {
      params: { start, end }
    });
    const data = response.data;

    // Events (Termine) cachen
    if (data.events && Array.isArray(data.events)) {
      try {
        // Extrahiere nur die Termin-Events für den Cache
        const termineEvents = data.events
          .filter(e => e.extendedProps?.type === 'termin' && e.extendedProps?.termin)
          .map(e => e.extendedProps.termin);

        if (termineEvents.length > 0) {
          await cacheMultiple(STORES.CACHE_TERMINE, termineEvents);
          await setMetadata(`${STORES.CACHE_TERMINE}-last-sync`, Date.now());
          console.log(`📦 ${termineEvents.length} Termine aus Kalender gecached`);
        }

        // Geschäftszeiten auch cachen
        if (data.geschaeftszeiten) {
          await cacheLegacyData('geschaeftszeiten', data.geschaeftszeiten, 1440);
        }
      } catch (cacheError) {
        console.warn('Kalender caching failed:', cacheError);
      }
    }

    return data;
  } catch (error) {
    // Bei Netzwerkfehler: Aus Cache laden und Events rekonstruieren
    if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.log('📴 Offline: Lade Kalender-Events aus Cache');

      const cachedTermine = await getAllCachedData(STORES.CACHE_TERMINE);
      const cachedGeschaeftszeiten = await getLegacyCachedData('geschaeftszeiten');

      if (cachedTermine.length > 0) {
        // Termine zu Kalender-Events konvertieren
        const events = cachedTermine.map(termin => {
          const startDate = new Date(termin.datum);
          if (termin.uhrzeit) {
            const [hours, minutes] = termin.uhrzeit.split(':');
            startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
          }

          const endDate = new Date(startDate);
          endDate.setMinutes(endDate.getMinutes() + (termin.dauerMinuten || 60));

          const isPrivat = !termin.kundeId;

          return {
            id: termin.id,
            title: isPrivat ? (termin.titel || 'Privat-Termin') : (termin.kunde?.name || 'Termin'),
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            backgroundColor: isPrivat ? '#6B7280' : '#057867',
            borderColor: isPrivat ? '#6B7280' : '#057867',
            extendedProps: {
              type: 'termin',
              termin: termin,
              kundeId: termin.kundeId,
              kundeName: termin.kunde?.name,
              titel: termin.titel,
              telefon: termin.kunde?.telefon,
              ort: termin.ort,
              beschreibung: termin.beschreibung,
              isPrivat: isPrivat
            }
          };
        });

        // Filtere nach Datumsbereich wenn möglich
        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        const filteredEvents = events.filter(event => {
          const eventStart = new Date(event.start);
          if (startDate && eventStart < startDate) return false;
          if (endDate && eventStart > endDate) return false;
          return true;
        });

        return {
          events: filteredEvents,
          geschaeftszeiten: cachedGeschaeftszeiten || []
        };
      }
    }
    throw error;
  }
};

// Verfügbare Zeitslots für einen Tag
export const getVerfuegbareZeiten = async (datum, dauerMinuten = 60) => {
  const response = await apiClient.get('/kalender/verfuegbar', {
    params: { datum, dauerMinuten }
  });
  return response.data;
};

// Geschäftszeiten (mit Cache)
export const getAllGeschaeftszeiten = async () => {
  try {
    const response = await apiClient.get('/geschaeftszeiten');
    const data = response.data;

    // Cachen
    try {
      await cacheLegacyData('geschaeftszeiten', data, 1440);
    } catch (cacheError) {
      console.warn('Geschaeftszeiten caching failed:', cacheError);
    }

    return data;
  } catch (error) {
    if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      const cached = await getLegacyCachedData('geschaeftszeiten');
      if (cached) return cached;
    }
    throw error;
  }
};

export const updateGeschaeftszeit = async (id, data) => {
  const response = await apiClient.put(`/geschaeftszeiten/${id}`, data);
  return response.data;
};

export const initializeGeschaeftszeiten = async () => {
  const response = await apiClient.post('/geschaeftszeiten/init');
  return response.data;
};

// Blockierte Zeiten
export const getAllBlockierteZeiten = async (startDatum, endDatum) => {
  const params = {};
  if (startDatum) params.startDatum = startDatum;
  if (endDatum) params.endDatum = endDatum;

  const response = await apiClient.get('/blockierte-zeiten', { params });
  return response.data;
};

export const getBlockierteZeitById = async (id) => {
  const response = await apiClient.get(`/blockierte-zeiten/${id}`);
  return response.data;
};

export const createBlockierteZeit = async (data) => {
  const response = await apiClient.post('/blockierte-zeiten', data);
  return response.data;
};

export const updateBlockierteZeit = async (id, data) => {
  const response = await apiClient.put(`/blockierte-zeiten/${id}`, data);
  return response.data;
};

export const deleteBlockierteZeit = async (id) => {
  const response = await apiClient.delete(`/blockierte-zeiten/${id}`);
  return response.data;
};

export const checkBlockiert = async (datum, uhrzeit) => {
  const response = await apiClient.get('/blockierte-zeiten/check', {
    params: { datum, uhrzeit }
  });
  return response.data;
};

// ICS-Datei importieren
export const importIcs = async (file) => {
  const formData = new FormData();
  formData.append('ics', file);
  const response = await apiClient.post('/kalender/import/ics', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Feiertage-Konfiguration der Org
export const getFeiertageConfig = async () => {
  const response = await apiClient.get('/kalender/feiertage-config');
  return response.data;
};
export const updateFeiertageConfig = async (data) => {
  const response = await apiClient.put('/kalender/feiertage-config', data);
  return response.data;
};
